import { useEffect, useRef, useState } from 'react';

import { CheckableElement } from '../../../checkable-elements/CheckableElement';
import { GrammarCheckAlert } from '../../../types';

const useAlertRects = (
  checkableElement: CheckableElement,
  targetEl: HTMLElement,
  containerEl: HTMLElement | null,
  alerts: GrammarCheckAlert[],
): { rects: DOMRect[][] } => {
  const [rects, setRects] = useState<DOMRect[][]>([]);
  const rectsRef = useRef<DOMRect[][]>([]);

  const rectsToRelativePosition = (rects: DOMRect[][]): DOMRect[][] => {
    if (!containerEl) {
      return rects;
    }

    const containerElRect = containerEl.getBoundingClientRect();

    return rects.map((alertRects) => {
      return alertRects.map((rect) => {
        return new DOMRect(
          rect.left - containerElRect.left,
          rect.top - containerElRect.top + (containerEl.scrollTop || 0),
          rect.width,
          rect.height,
        );
      });
    });
  };

  const recalculateRects = (): void => {
    if (!alerts || alerts.length === 0) {
      setRects([]);
      rectsRef.current = [];
      return;
    }

    const calculatedRects = rectsToRelativePosition(
      checkableElement.calculateAlertClientRects(alerts),
    );
    setRects(calculatedRects);
    rectsRef.current = calculatedRects;
  };

  useEffect(() => {
    recalculateRects();

    if (targetEl) {
      const handleScroll = (): void => {
        recalculateRects();
      };

      targetEl.addEventListener('scroll', handleScroll);

      return (): void => {
        targetEl.removeEventListener('scroll', handleScroll);
      };
    }
  }, [targetEl, containerEl, alerts]);

  return { rects };
};

export default useAlertRects;
